import type { components } from "../../api/generated-schema";
import { apiRequest, createRequestId } from "../../api/httpClient";

export type OrganizationTreeNode = components["schemas"]["OrganizationTreeNode"];
export type OrganizationDetail = components["schemas"]["OrganizationAdmin"];
export type OrganizationVersionView = components["schemas"]["OrganizationVersion"];
export type OrganizationCommandResult = components["schemas"]["OrganizationCommandResult"];
export type OrganizationMergeResult = components["schemas"]["OrganizationMergeResult"];
export type CreateOrganizationRequest = components["schemas"]["CreateOrganizationRequest"];
export type OpenOrganizationRequest = components["schemas"]["OpenOrganizationRequest"];
export type CloseOrganizationRequest = components["schemas"]["CloseOrganizationRequest"];
export type RenameOrganizationRequest = components["schemas"]["RenameOrganizationRequest"];
export type TransferOrganizationRequest = components["schemas"]["TransferOrganizationRequest"];
export type MergeOrganizationRequest = components["schemas"]["MergeOrganizationRequest"];

const BASE_PATH = "/admin/organizations";

function asOfQuery(asOf?: string): string {
  return asOf ? `?${new URLSearchParams({ asOf }).toString()}` : "";
}

function commandHeaders(idempotencyKey: string): Record<string, string> {
  return { "Idempotency-Key": idempotencyKey, "X-Request-Id": createRequestId() };
}

function signalInit(signal?: AbortSignal): RequestInit {
  return signal ? { signal } : {};
}

function postCommand<T>(path: string, body: unknown, idempotencyKey: string): Promise<T> {
  return apiRequest<T>(path, {
    method: "POST",
    headers: commandHeaders(idempotencyKey),
    body: JSON.stringify(body),
  });
}

export function getOrganizationTree(
  asOf?: string,
  signal?: AbortSignal,
): Promise<OrganizationTreeNode[]> {
  return apiRequest<OrganizationTreeNode[]>(`${BASE_PATH}${asOfQuery(asOf)}`, signalInit(signal));
}

export function getOrganizationDetail(
  id: string,
  asOf?: string,
  signal?: AbortSignal,
): Promise<OrganizationDetail> {
  return apiRequest<OrganizationDetail>(
    `${BASE_PATH}/${encodeURIComponent(id)}${asOfQuery(asOf)}`,
    signalInit(signal),
  );
}

export function getOrganizationHistory(
  id: string,
  signal?: AbortSignal,
): Promise<OrganizationVersionView[]> {
  return apiRequest<OrganizationVersionView[]>(
    `${BASE_PATH}/${encodeURIComponent(id)}/history`,
    signalInit(signal),
  );
}

export function createOrganization(
  body: CreateOrganizationRequest,
  idempotencyKey: string,
): Promise<OrganizationCommandResult> {
  return postCommand<OrganizationCommandResult>(BASE_PATH, body, idempotencyKey);
}

export function openOrganization(
  id: string,
  body: OpenOrganizationRequest,
  idempotencyKey: string,
): Promise<OrganizationCommandResult> {
  return postCommand<OrganizationCommandResult>(
    `${BASE_PATH}/${encodeURIComponent(id)}/open`,
    body,
    idempotencyKey,
  );
}

export function closeOrganization(
  id: string,
  body: CloseOrganizationRequest,
  idempotencyKey: string,
): Promise<OrganizationCommandResult> {
  return postCommand<OrganizationCommandResult>(
    `${BASE_PATH}/${encodeURIComponent(id)}/close`,
    body,
    idempotencyKey,
  );
}

export function renameOrganization(
  id: string,
  body: RenameOrganizationRequest,
  idempotencyKey: string,
): Promise<OrganizationCommandResult> {
  return postCommand<OrganizationCommandResult>(
    `${BASE_PATH}/${encodeURIComponent(id)}/rename`,
    body,
    idempotencyKey,
  );
}

export function transferOrganization(
  id: string,
  body: TransferOrganizationRequest,
  idempotencyKey: string,
): Promise<OrganizationCommandResult> {
  return postCommand<OrganizationCommandResult>(
    `${BASE_PATH}/${encodeURIComponent(id)}/transfer`,
    body,
    idempotencyKey,
  );
}

export function mergeOrganization(
  survivorId: string,
  body: MergeOrganizationRequest,
  idempotencyKey: string,
): Promise<OrganizationMergeResult> {
  return postCommand<OrganizationMergeResult>(
    `${BASE_PATH}/${encodeURIComponent(survivorId)}/merge`,
    body,
    idempotencyKey,
  );
}
